import React, { useState } from 'react';
import { FileText, Send, CheckCircle, Globe, Calendar, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const VisaForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    passportNumber: '',
    passportExpiry: '',
    country: '',
    visaType: '',
    travelDate: '',
    returnDate: '',
    purposeOfVisit: '',
    additionalInfo: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const countries = [
    'Dubai (UAE)', 'Thailand', 'Singapore', 'Malaysia', 'Bali (Indonesia)', 
    'Sri Lanka', 'Maldives', 'Nepal', 'Bhutan', 'Vietnam', 
    'Turkey', 'Egypt', 'Australia', 'USA', 'UK', 'Canada', 'Schengen (Europe)'
  ];

  const visaTypes = [
    'Tourist Visa',
    'Business Visa',
    'Student Visa',
    'Work Visa',
    'Transit Visa',
    'Medical Visa'
  ];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/visa/application`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          date_of_birth: formData.dateOfBirth,
          passport_number: formData.passportNumber,
          passport_expiry: formData.passportExpiry,
          country: formData.country,
          visa_type: formData.visaType,
          travel_date: formData.travelDate,
          return_date: formData.returnDate,
          purpose_of_visit: formData.purposeOfVisit,
          additional_info: formData.additionalInfo
        }),
      });

      if (response.ok) {
        setIsSubmitting(false);
        setIsSubmitted(true);
        
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            dateOfBirth: '',
            passportNumber: '',
            passportExpiry: '',
            country: '',
            visaType: '',
            travelDate: '',
            returnDate: '',
            purposeOfVisit: '',
            additionalInfo: ''
          });
        }, 3000);
      } else {
        throw new Error('Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting visa application:', error);
      setIsSubmitting(false);
      alert('Failed to submit visa application. Please try again.');
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="relative h-80 mb-20 overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=1200" 
          alt="Visa Services"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 to-red-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center justify-center text-center text-white">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Tourist Visa Application
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto">
              Hassle-free visa assistance for your international travel
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Info Section */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Visa Services</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Globe size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Multiple Countries</h3>
                    <p className="text-sm text-gray-600">We assist with visa applications for 50+ countries worldwide</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FileText size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Documentation Help</h3>
                    <p className="text-sm text-gray-600">Complete assistance with document preparation and verification</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Calendar size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Quick Processing</h3>
                    <p className="text-sm text-gray-600">Fast-track visa processing with regular updates</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                    <User size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Expert Guidance</h3>
                    <p className="text-sm text-gray-600">Dedicated visa consultants to guide you throughout</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-600 to-blue-600 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-4">Need Help?</h3>
              <p className="text-sm mb-4">Our visa experts are here to assist you</p>
              <div className="space-y-2 text-sm">
                <p>📞 +91-8283950765</p>
                <p>📞 +91-7837714489</p>
                <p>📧 info@tumholidays.com</p>
              </div>
            </div>
          </div>

          {/* Visa Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Apply for Tourist Visa</h2>
              <p className="text-gray-600 mb-8">Fill out the form below and our team will contact you within 24 hours</p>

              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle size={48} className="text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
                  <p className="text-gray-600">Thank you for your visa application. Our team will review and contact you soon!</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="fullName">Full Name (as per passport) *</Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          placeholder="Enter full name"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                        <Input
                          id="dateOfBirth"
                          name="dateOfBirth"
                          type="date"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your.email@example.com"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91-XXXXXXXXXX"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Passport Information */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Passport Information</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="passportNumber">Passport Number *</Label>
                        <Input
                          id="passportNumber"
                          name="passportNumber"
                          value={formData.passportNumber}
                          onChange={handleChange}
                          required
                          placeholder="Enter passport number"
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="passportExpiry">Passport Expiry Date *</Label>
                        <Input
                          id="passportExpiry"
                          name="passportExpiry"
                          type="date"
                          value={formData.passportExpiry}
                          onChange={handleChange}
                          required
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Visa Details */}
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4 mt-8">Visa Details</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="country">Country *</Label>
                        <Select 
                          value={formData.country} 
                          onValueChange={(value) => setFormData({...formData, country: value})}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country} value={country}>{country}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="visaType">Visa Type *</Label>
                        <Select 
                          value={formData.visaType} 
                          onValueChange={(value) => setFormData({...formData, visaType: value})}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Select visa type" />
                          </SelectTrigger>
                          <SelectContent>
                            {visaTypes.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Travel Dates */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="travelDate">Intended Travel Date *</Label>
                      <Input
                        id="travelDate"
                        name="travelDate"
                        type="date"
                        value={formData.travelDate}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="returnDate">Expected Return Date *</Label>
                      <Input
                        id="returnDate"
                        name="returnDate"
                        type="date"
                        value={formData.returnDate}
                        onChange={handleChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Purpose and Additional Info */}
                  <div>
                    <Label htmlFor="purposeOfVisit">Purpose of Visit *</Label>
                    <Input
                      id="purposeOfVisit"
                      name="purposeOfVisit"
                      value={formData.purposeOfVisit}
                      onChange={handleChange}
                      required
                      placeholder="e.g., Tourism, Business, Visit Family"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="additionalInfo">Additional Information</Label>
                    <Textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      value={formData.additionalInfo}
                      onChange={handleChange}
                      rows={4}
                      placeholder="Any additional information or special requirements..."
                      className="mt-2"
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-red-600 hover:from-blue-700 hover:to-red-700 text-white text-lg py-6 transition-all duration-300 hover:scale-105"
                  >
                    {isSubmitting ? (
                      <>
                        <span className="animate-spin mr-2">⏳</span>
                        Submitting Application...
                      </>
                    ) : (
                      <>
                        <Send className="mr-2" size={20} />
                        Submit Visa Application
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisaForm;